import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {hasProperty, isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {findContentUpdatedAt} from '../../../helpers/models/conversationHelpers';
import {
  ConversationTimelineBoundary,
  ConversationTimelineMetadata,
  isActivityDateWithinConversationTimelineRange,
} from '../../../helpers/viewerHelpers';
import {DataGroupModel} from '../../../models/api/dataGroupModel';
import {AppState} from '../../appState';
import {MAX_NUMBER_OF_CONVERSATIONS_PER_REQUEST} from '../../data/conversations/conversationsHelpers';
import {getConversations} from '../../data/dataSelectors';
import {getUserId} from '../../data/teammates/teammatesSelectors';
import {InboxesVisualConversationSelection} from '../cells/helpers/cellsPanelSelectionHelpers';
import {InboxesParameters, InboxesParametersActivity} from '../inboxes/parameters/inboxesParameters';
import {computeIsAllExceptSelection} from '../inboxes/parameters/inboxesParametersHelpers';
import {
  ViewerPanelConversationPageState,
  ViewerPanelPageState,
  ViewerPanelPageTypesEnum,
} from './viewerPanelState';

/*
 * Types.
 */

export interface ConversationContentFetchOptions {
  /** The timestamp used to check for a cache hit/miss. */
  contentUpdatedAt: number;
  /** The timestamp used to store in the cache and evict from the cache. */
  cacheTimestamp: number;
}

/*
 * Helpers.
 */

export function computeConversationContentFetchOptions(
  state: AppState,
  conversationId: number,
): ConversationContentFetchOptions | undefined {
  const userId = getUserId(state);
  const conversations = getConversations(state);
  const conversation = conversations[conversationId];
  if (!conversation) {
    return undefined;
  }

  const contentUpdatedAt = findContentUpdatedAt(conversation.contentUpdatedDates, userId);

  const tracker = conversation.trackers[userId];
  const cacheTimestamp = Math.max(tracker ? tracker.bumpedAt : 0, contentUpdatedAt);

  return {
    contentUpdatedAt,
    cacheTimestamp,
  };
}

// TODO: Update runtime types to ensure these data groups have timeline metadata.
const defaultTimeline: ConversationTimelineMetadata = {
  activityIds: [],
  topBoundary: undefined,
  bottomBoundary: undefined,
};

export function fetchInitialConversationFromDataGroup(
  dataGroup: DataGroupModel,
): ConversationTimelineMetadata {
  const {timeline = defaultTimeline} = dataGroup;

  return timeline;
}

export function fetchMoreConversationFromDataGroup(
  direction: ScrollDirectionsEnum,
  referenceBoundary: ConversationTimelineBoundary,
  dataGroup: DataGroupModel,
) {
  const {timeline = defaultTimeline} = dataGroup;

  if (direction === ScrollDirectionsEnum.TOP) {
    return {
      activityIds: timeline.activityIds,
      topBoundary: timeline.topBoundary,
      bottomBoundary: referenceBoundary,
    };
  }

  return {
    activityIds: timeline.activityIds,
    topBoundary: referenceBoundary,
    bottomBoundary: timeline.bottomBoundary,
  };
}

export function isConversationAndActivityOnPage(
  page: ViewerPanelPageState,
  activity: InboxesParametersActivity,
): boolean {
  if (
    page.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ||
    page.status !== AsyncStatusesEnum.LOADED
  ) {
    return false;
  }

  if (hasProperty(activity, 'id')) {
    return isActivityIdOnPage(page, activity.id);
  }

  return isActivityDateOnPage(page, activity.date);
}

function isActivityIdOnPage(page: ViewerPanelConversationPageState, activityId: number): boolean {
  return isDefined(page.activities[activityId]);
}

function isActivityDateOnPage(page: ViewerPanelConversationPageState, aroundActivityDate: number): boolean {
  return isActivityDateWithinConversationTimelineRange(page, aroundActivityDate);
}

export function computeIsBulkActionRequired(
  inboxesParameters: InboxesParameters,
  selection: InboxesVisualConversationSelection,
): boolean {
  return (
    computeIsAllExceptSelection(inboxesParameters.selection) &&
    (!selection.isFullyKnown || selection.knownIds.length > MAX_NUMBER_OF_CONVERSATIONS_PER_REQUEST)
  );
}
